import React, { useMemo } from 'react';
import { motion } from 'motion/react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme, TypographyProps } from '@mui/material';
import { stagger as staggerTokens } from '../theme/tokens';
import { IN_VIEW_MARGIN, wordContainer, wordItem } from './variants';
import { useMotionSafe } from './useMotionSafe';

interface AnimatedTextProps
  extends Omit<TypographyProps, 'children' | 'onAnimationStart' | 'onAnimationEnd' | 'onDrag'> {
  /** The text to animate. Plain string so it can be split safely. */
  text: string;
  /** Reveal one word at a time, or the whole line as a block. */
  by?: 'word' | 'line';
  /** Seconds before the first word appears. */
  delay?: number;
  /** Seconds between each word. */
  interval?: number;
  /** Fire on scroll-into-view rather than immediately on mount. */
  whenInView?: boolean;
  /** Render a highlighted span for these words (matched case-insensitively). */
  highlight?: string[];
  /** Style of a highlighted word; the primary-to-secondary gradient by default. */
  highlightSx?: SxProps<Theme>;
}

const gradientHighlight: SxProps<Theme> = {
  background: (t) => `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const MotionSpan = motion.span;

/**
 * Motion versions of the tags a headline renders as. The animated wrapper has
 * to be the semantic element itself: passing `component` straight through to
 * Typography replaced the motion element, and no word ever animated.
 */
const MOTION_TAGS: Record<string, React.ElementType> = {
  div: motion.div,
  h1: motion.h1,
  h2: motion.h2,
  h3: motion.h3,
  h4: motion.h4,
  p: motion.p,
  span: motion.span,
};

/**
 * Headline that reveals word by word.
 *
 * Each word rises from behind a clipping mask, which reads far more
 * deliberate than fading a whole paragraph at once. With `by="line"`, each
 * line of `text` (split on newlines) is its own row and reveals as a unit.
 * Collapses to plain text when the viewer prefers reduced motion.
 */
export const AnimatedText: React.FC<AnimatedTextProps> = ({
  text,
  by = 'word',
  delay = 0,
  interval = staggerTokens.base,
  whenInView = false,
  highlight = [],
  highlightSx = gradientHighlight,
  component,
  sx,
  ...typographyProps
}) => {
  const { enabled } = useMotionSafe();

  const pieces = useMemo(() => (by === 'word' ? text.split(' ') : text.split('\n')), [text, by]);
  const highlighted = useMemo(
    () => new Set(highlight.map((w) => w.toLowerCase().replace(/[^\w]/g, ''))),
    [highlight],
  );

  const isHighlighted = (piece: string) =>
    highlighted.has(piece.toLowerCase().replace(/[^\w]/g, ''));

  if (!enabled) {
    // Highlighting is styling, not motion, so it stays.
    return (
      <Typography {...(component ? { component } : {})} sx={sx} {...typographyProps}>
        {highlighted.size === 0 && by === 'word'
          ? text
          : pieces.map((piece, index) => (
              <React.Fragment key={`${piece}-${index}`}>
                {isHighlighted(piece) ? (
                  <Box component="span" sx={highlightSx}>
                    {piece}
                  </Box>
                ) : (
                  piece
                )}
                {index < pieces.length - 1 && (by === 'word' ? ' ' : <br />)}
              </React.Fragment>
            ))}
      </Typography>
    );
  }

  const animationProps = whenInView
    ? ({ initial: 'hidden', whileInView: 'visible', viewport: { once: true, margin: IN_VIEW_MARGIN } } as const)
    : ({ initial: 'hidden', animate: 'visible' } as const);

  return (
    <Typography
      component={(typeof component === 'string' && MOTION_TAGS[component]) || motion.div}
      variants={wordContainer(interval, delay)}
      {...animationProps}
      sx={sx}
      {...typographyProps}
    >
      {pieces.map((piece, index) => (
        // The wrapper clips, so the word slides up out of nowhere.
        <Box
          key={`${piece}-${index}`}
          component="span"
          sx={{
            display: by === 'line' ? 'block' : 'inline-block',
            overflow: 'hidden',
            verticalAlign: 'bottom',
            paddingBottom: '0.06em',
          }}
        >
          <MotionSpan
            variants={wordItem}
            style={{
              display: 'inline-block',
              willChange: 'transform, opacity',
            }}
          >
            <Box component="span" sx={isHighlighted(piece) ? highlightSx : undefined}>
              {piece}
            </Box>
            {index < pieces.length - 1 && ' '}
          </MotionSpan>
        </Box>
      ))}
    </Typography>
  );
};

export default AnimatedText;
